import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, map } from 'rxjs';
import { UsuarioDTO } from './autenticacion.models';
import { TokenService } from './token.service';
import {
  ListaDTO,
  Parametros,
} from 'src/app/compartido/utilidades/compartido.models';
import { AutorizacionService } from 'src/app/nucleo/autorizacion/autorizacion.service';
import { Router } from '@angular/router';
import { OrganizacionService } from '../utilidades/organizacion.service';
import { BienvenidaComponent } from 'src/app/adhoc/componentes/bienvenida/bienvenida.component';
import {
  ClienteDTO,
  DireccionDTO,
} from 'src/app/clientes/utilidades/clientes.models';
import { ConfiguracionDialogoVistaPrevia } from 'src/app/compartido/dialogos/dialogo-vista-previa/utilidades/dialogo-vista-previa.static';
import { RolUsuario } from '../autorizacion/autorizacion.models';
import { DialogosService } from '../utilidades/dialogos.service';
import { NotificacionesService } from '../header/utilidades/notificaciones.service';
import { ErrorFrontend } from 'src/app/compartido/utilidades/error.utils';

@Injectable({
  providedIn: 'root',
  deps: [
    HttpClient,
    TokenService,
    AutorizacionService,
    OrganizacionService,
    DialogosService,
    NotificacionesService,
    Router,
  ],
})
export class AutenticacionService {
  /** Usuario */
  private _usuario: UsuarioDTO | undefined;
  private _usuarioPromise$: Promise<UsuarioDTO | undefined> | undefined;
  public get usuario$(): Promise<UsuarioDTO | undefined> {
    if (this._usuarioPromise$) return this._usuarioPromise$;
    if (this._usuario) return Promise.resolve(this._usuario);
    this._usuarioPromise$ = this._obtenerUsuario$().then(
      (usuario: UsuarioDTO | undefined) => {
        this._usuario = usuario;
        this._usuarioPromise$ = undefined;
        return usuario;
      },
    );
    return this._usuarioPromise$;
  }

  constructor(
    private readonly _http: HttpClient,
    private readonly _tokenService: TokenService,
    private readonly _autorizacionService: AutorizacionService,
    private readonly _organizacionService: OrganizacionService,
    private readonly _dialogosService: DialogosService,
    private readonly _notificacionesService: NotificacionesService,
    private readonly _router: Router,
  ) {}

  /**
   * Obtiene el usuario autenticado del servidor.
   * @returns {Promise<UsuarioDTO | undefined>} - El usuario autenticado, si existe (promise).
   * @author Juan Corral
   */
  private async _obtenerUsuario$(): Promise<UsuarioDTO | undefined> {
    return await firstValueFrom(
      this._http.get<UsuarioDTO>('autenticacion/usuarios/perfil/'),
    ).catch(() => undefined);
  }

  /**
   * Obtiene los usuarios del servidor.
   * @param {Parametros} filtros - Filtros para la búsqueda de usuarios.
   * @returns {ListaDTO<UsuarioDTO>} - Lista de usuarios (promise).
   * @throws {ErrorFrontend} - Si ocurre un error al obtener los usuarios.
   * @author Juan Corral
   */
  public async obtenerUsuarios$(
    filtros: Parametros,
  ): Promise<ListaDTO<UsuarioDTO>> {
    return await firstValueFrom(
      this._http.get<ListaDTO<UsuarioDTO>>('autenticacion/usuarios/', {
        params: filtros,
      }),
    ).catch((error: HttpErrorResponse) => {
      throw new ErrorFrontend(error);
    });
  }

  /**
   * Cierra sesión del usuario en el servidor.
   * @author Juan Corral
   */
  public async logout$(): Promise<void> {
    await firstValueFrom(
      this._http.post('autenticacion/logout/', {
        token: this._tokenService.refreshToken,
      }),
    ).catch(() => undefined);
    this.limpiarUsuario();
    this._tokenService.limpiarToken();
    this._autorizacionService.limpiarPermisos();
    this._organizacionService.limpiarOrganizaciones();
    this._notificacionesService.limpiarNotificaciones();
    this._router.navigate(['ingreso', 'login']);
  }

  /**
   * Limpia el usuario autenticado.
   * @autor Juan Corral
   */
  public limpiarUsuario(): void {
    this._usuario = undefined;
    this._usuarioPromise$ = undefined;
  }

  /**
   * Verifica si el cliente está incompleto y muestra un diálogo de bienvenida si es necesario.
   * @author Juan Corral
   */
  private async verificarCliente$(): Promise<void> {
    // Cliente incompleto
    const usuario = await this.usuario$;
    if (
      usuario!.rol === RolUsuario.CLIENTE &&
      this._clienteIncompleto(usuario!.cliente!)
    ) {
      const config: ConfiguracionDialogoVistaPrevia = {
        componente: BienvenidaComponent,
        output: 'completado',
      };
      this._dialogosService.abrirVistaPrevia$(config, { disableClose: true });
    }
  }

  /**
   * Devuelve si el cliente está incompleto.
   * @param {ClienteDTO} cliente - El cliente a verificar.
   * @returns {boolean} - Si el cliente está incompleto o no.
   * @author Juan Corral
   */
  private _clienteIncompleto(cliente: ClienteDTO): boolean {
    const direccionIncompleta = (direccion: DireccionDTO) => {
      return (
        !direccion.detalles ||
        !direccion.barrio ||
        !direccion.localidad ||
        !direccion.ciudad ||
        !direccion.departamento
      );
    };
    const direcciones = cliente.direcciones.filter(
      (direccion) => direccion.creacion_manual,
    );
    return (
      !cliente ||
      !cliente.direcciones ||
      direcciones.length === 0 ||
      direcciones.some((direccion) => direccionIncompleta(direccion))
    );
  }
}
